import React, { useEffect, useState } from 'react';
import '../styles/BalanceSheet.css';
import fetchApi from '../utils/api.js';

const BalanceSheet = ({ customer, onBack, customerId, isPurchase, isTransporter, startDate, endDate, companyId }) => {
    const [ledger, setLedger] = useState([]);
    const [totals, setTotals] = useState({ debit: 0, credit: 0, balance: 0 });
    const [loading, setLoading] = useState(false);
    const [company, setCompany] = useState(null);

    useEffect(() => {
        if (customerId) {
            fetchStatement();
            fetchCompany();
        }
    }, [customerId, startDate, endDate, companyId]);

    const fetchCompany = async () => {
        try {
            // 1. If explicit companyId is passed (e.g. from inline dashboard), fetch that company directly!
            const targetCompanyId = companyId;
            if (targetCompanyId) {
                const res = await fetchApi(`/company/${targetCompanyId}`);
                if (res) {
                    setCompany(res.company || res);
                    return;
                }
            }

            // 2. Otherwise fall back to user's company id from session
            const userStr = sessionStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                const userCompanyId = user.company_id || user.companyId;

                if (userCompanyId) {
                    // Fetch specific company
                    const res = await fetchApi(`/company/${userCompanyId}`);
                    if (res) {
                        // The endpoint /company/:id might return the company directly
                        setCompany(res.company || res);
                    }
                } else if (user.role === 'SUPER_ADMIN') {
                    // Super admin fallback: get all companies and take first
                    const res = await fetchApi('/company');
                    if (res && res.companies && res.companies.length > 0) {
                        setCompany(res.companies[0]);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching company details:", error);
        }
    };

    const fetchStatement = async () => {
        try {
            setLoading(true);
            let baseRoute = '/invoice-payment';
            if (isPurchase) baseRoute = '/purchase-order-payment';
            if (isTransporter) baseRoute = '/transporter-payment';

            const queryPart = (startDate && endDate) ? `?startDate=${startDate}&endDate=${endDate}` : '';
            const data = await fetchApi(`${baseRoute}/ledger/${customerId}${queryPart}`);
            setLedger(data);

            const totalsData = await fetchApi(`${baseRoute}/totals/${customerId}${queryPart}`);
            setTotals({
                debit: totalsData.totalDebit || totalsData.totalInvoiceAmount || 0,
                credit: totalsData.totalCredit || totalsData.paidAmount || 0,
                balance: totalsData.balance || totalsData.pendingAmount || 0
            });
        } catch (error) {
            console.error("Error fetching statement:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Filter entries: Left Side = Invoices/Service Charges, Right Side = Payments
    let leftEntries = [];
    let rightEntries = [];

    if (isPurchase) {
        // For Sellers: Invoices are credits (we owe them), Payments are debits (we paid them)
        leftEntries = ledger.filter(tx => (parseFloat(tx.credit) || 0) > 0 || tx.type === 'Invoice');
        rightEntries = ledger.filter(tx => (parseFloat(tx.debit) || 0) > 0 || tx.type === 'Payment');
    } else {
        // For Customers & Transporters: Invoices are debits, Payments are credits
        leftEntries = ledger.filter(tx => (parseFloat(tx.debit) || 0) > 0 || tx.type.includes('Invoice') || tx.type.includes('Order'));
        rightEntries = ledger.filter(tx => (parseFloat(tx.credit) || 0) > 0 || tx.type === 'Payment');
    }

    // Combine them into a T-Ledger row format
    const ledgerRows = [];
    const maxRows = Math.max(leftEntries.length, rightEntries.length);

    for (let i = 0; i < maxRows; i++) {
        ledgerRows.push({
            left: leftEntries[i] || null,
            right: rightEntries[i] || null
        });
    }

    // Logic for DR/CR Closing Balance
    const balance = totals.balance;
    const isDr = balance > 0;
    const absBalance = Math.abs(balance);

    const renderEntry = (entry, isLeft) => {
        if (!entry) return null;
        return (
            <div className="bs-entry">
                <div className="bs-entry-top-line">
                    <span className="bs-entry-amount">
                        {(parseFloat(entry.debit) || parseFloat(entry.credit) || 0).toFixed(2)}
                    </span>
                    <span className="bs-date">
                        {entry.date ? new Date(entry.date).toLocaleDateString('en-GB') : ''}
                    </span>
                </div>
                <div className="bs-entry-details">
                    {isLeft ? (
                        <>
                            <div className="bs-detail-item">
                                {entry.type === 'Invoice' ? `INVOICE NO: ${entry.refNo || 'N/A'}` : `REF: ${entry.refNo || 'N/A'}`}
                            </div>
                            <div className="bs-detail-item">
                                TOTAL: {(parseFloat(entry.debit) || parseFloat(entry.credit) || 0).toFixed(2)}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="bs-detail-item">
                                VOUCHER: {entry.voucherType || entry.type || 'N/A'}
                            </div>
                            <div className="bs-detail-item">
                                BANK: {entry.bank || 'N/A'}
                            </div>
                            <div className="bs-detail-item">
                                COLLECTOR: {entry.paymentCollectorName || 'S.O.'}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="balance-sheet-container">
            {/* Header / Actions */}
            <div className="bs-actions no-print">
                <button className="bs-btn-back" onClick={onBack}>Back</button>
                <button className="bs-btn-print" onClick={handlePrint}>Print</button>
            </div>

            <div className="bs-printable-content">
                <div className="bs-company-header">
                    <h1>{company?.companyName || 'COMPANY NAME'}</h1>
                    <p>GSTIN No: {company?.companyGstNumber || '---'}</p>
                    <p>{company?.companyAddress || '---'}</p>
                </div>

                <div className="bs-statement-for">
                    <h3>Account Statement For {customer.tradeName || customer.customerName || 'CUSTOMER NAME'}</h3>
                </div>

                <div className="bs-t-ledger">
                    <div className="bs-t-header">
                        <div className="bs-half">CREDIT PARTICULARS</div>
                        <div className="bs-half">DEBIT PARTICULARS</div>
                    </div>

                    <div className="bs-t-body">
                        {ledgerRows.map((row, idx) => (
                            <div className="bs-t-row" key={idx}>
                                {/* Credit Side (Left) - Invoices */}
                                <div className="bs-half bs-entry-side">
                                    {renderEntry(row.left, true)}
                                </div>

                                {/* Debit Side (Right) - Payments */}
                                <div className="bs-half bs-entry-side">
                                    {renderEntry(row.right, false)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer Totals & Balance */}
                    <div className="bs-t-footer">
                        {/* Summary Line */}
                        <div className="bs-t-row bs-summary-row">
                            <div className="bs-half">
                                <span className="bs-total-amount">
                                    {(parseFloat(isPurchase ? totals.credit : totals.debit)).toFixed(2)}
                                </span>
                            </div>
                            <div className="bs-half">
                                <span className="bs-total-amount">
                                    {(parseFloat(isPurchase ? totals.debit : totals.credit)).toFixed(2)}
                                </span>
                            </div>
                        </div>

                        {/* Balance Line */}
                        <div className="bs-t-row bs-balance-row">
                            <div className="bs-half">
                                {/* Left side balance should appear if the RIGHT side total was higher */}
                                {isPurchase ? (
                                    // Seller: If Debit (Payments) > Credit (Invoices) -> Debit Balance on Left
                                    balance < 0 && (
                                        <div className="bs-balance-line">
                                            <span className="bs-total-amount">{absBalance.toFixed(2)}</span>
                                            <span className="bs-balance-label">DR CLOSING BALANCE</span>
                                        </div>
                                    )
                                ) : (
                                    // Customer/Transporter: If Credit (Payments) > Debit (Invoices) -> Credit Balance on Left
                                    balance < 0 && (
                                        <div className="bs-balance-line">
                                            <span className="bs-total-amount">{absBalance.toFixed(2)}</span>
                                            <span className="bs-balance-label">CR CLOSING BALANCE</span>
                                        </div>
                                    )
                                )}
                            </div>
                            <div className="bs-half">
                                {/* Right side balance should appear if the LEFT side total was higher */}
                                {isPurchase ? (
                                    // Seller: If Credit (Invoices) > Debit (Payments) -> Credit Balance on Right
                                    balance > 0 && (
                                        <div className="bs-balance-line">
                                            <span className="bs-total-amount">{absBalance.toFixed(2)}</span>
                                            <span className="bs-balance-label">CR CLOSING BALANCE</span>
                                        </div>
                                    )
                                ) : (
                                    // Customer/Transporter: If Debit (Invoices) > Credit (Payments) -> Debit Balance on Right
                                    balance > 0 && (
                                        <div className="bs-balance-line">
                                            <span className="bs-total-amount">{absBalance.toFixed(2)}</span>
                                            <span className="bs-balance-label">DR CLOSING BALANCE</span>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>

                        {/* Final Tally */}
                        <div className="bs-t-row bs-tally-row">
                            <div className="bs-half">
                                <div className="bs-tally-amount-box">
                                    <span className="bs-total-amount">
                                        {Math.max(totals.credit, totals.debit).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                            <div className="bs-half">
                                <div className="bs-tally-amount-box">
                                    <span className="bs-total-amount">
                                        {Math.max(totals.credit, totals.debit).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BalanceSheet;
